import React, { useState } from 'react';
import { Lock, Unlock, Search, CheckCircle2, XCircle, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const ALL_STUDENTS = [
  { id: 1, name: 'Aisha Patel', email: 'aisha@email.com', progress: 78, enrolled: '2026-01-10', lastActive: '2 hours ago', status: 'active', access: true, score: 82 },
  { id: 2, name: 'Marcus Johnson', email: 'marcus@email.com', progress: 45, enrolled: '2026-01-22', lastActive: '1 day ago', status: 'active', access: true, score: 67 },
  { id: 3, name: 'Lisa Chang', email: 'lisa@email.com', progress: 100, enrolled: '2025-12-15', lastActive: '3 days ago', status: 'completed', access: true, score: 95 },
  { id: 4, name: 'Raj Gupta', email: 'raj@email.com', progress: 20, enrolled: '2026-02-01', lastActive: '5 days ago', status: 'inactive', access: false, score: 40 },
  { id: 5, name: 'Sophie Martinez', email: 'sophie@email.com', progress: 62, enrolled: '2026-01-05', lastActive: '4 hours ago', status: 'active', access: true, score: 74 },
  { id: 6, name: 'Priya Nair', email: 'priya@email.com', progress: 33, enrolled: '2026-02-10', lastActive: '6 hours ago', status: 'active', access: true, score: 55 },
];

export default function StudentAccessPanel({ course }) {
  const [students, setStudents] = useState(ALL_STUDENTS);
  const [search, setSearch] = useState('');
  const [notified, setNotified] = useState(null);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  const toggleAccess = (id) => {
    setStudents(prev => prev.map(s => s.id === id ? { ...s, access: !s.access } : s));
  };

  const sendNotification = (id) => {
    setNotified(id);
    setTimeout(() => setNotified(null), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold">Student Access & Progress</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{course.title} · Grant or restrict student access</p>
        </div>
        <div className="flex gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> {students.filter(s => s.access).length} with access</span>
          <span className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-red-400" /> {students.filter(s => !s.access).length} restricted</span>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search students..." className="pl-9" />
      </div>

      <div className="space-y-2">
        {filtered.map(s => (
          <div key={s.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${s.access ? 'bg-card' : 'bg-red-50/50 border-red-100'}`}>
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary text-sm flex-shrink-0">{s.name[0]}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm truncate">{s.name}</p>
                <Badge className={`text-xs border-0 ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                  {s.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate">{s.email} · Last active {s.lastActive}</p>
            </div>

            {/* Progress */}
            <div className="hidden sm:block w-28">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground">Progress</span>
                <span className="text-xs font-semibold">{s.progress}%</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${s.progress}%` }} />
              </div>
            </div>

            {/* Score */}
            <div className="hidden md:flex flex-col items-center w-12">
              <span className="text-xs text-muted-foreground">Score</span>
              <span className={`text-sm font-bold ${s.score >= 80 ? 'text-green-600' : s.score >= 60 ? 'text-amber-600' : 'text-red-500'}`}>{s.score}</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Button
                size="sm"
                variant={s.access ? 'outline' : 'default'}
                className={`gap-1.5 h-8 text-xs ${!s.access ? 'bg-green-600 hover:bg-green-700 text-white border-0' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                onClick={() => toggleAccess(s.id)}
              >
                {s.access ? <><Lock className="w-3 h-3" /> Restrict</> : <><Unlock className="w-3 h-3" /> Grant</>}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={`h-8 w-8 ${notified === s.id ? 'text-green-600' : 'text-muted-foreground'}`}
                onClick={() => sendNotification(s.id)}
                title="Send notification"
              >
                <Bell className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}